import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronUp,
  Clock,
  Download,
  FilePlus,
  FileWarning,
  History,
  Loader,
  Minus,
  MoreHorizontal,
  MoreVertical,
  PanelRightClose,
  Plus,
  RefreshCw,
  Save,
  Send,
  Sparkles,
  Trash2,
  X,
  type LucideIcon
} from "lucide-react";
import { toast } from "sonner";
import { BrandLoader, LoadingState } from "@/components/brand";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { EmptyState } from "@/components/ui/empty-state";
import { ReconnectingState } from "@/components/ui/reconnecting-state";
import { isTransientError } from "@/api/retryPolicy";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PaneHeader } from "@/components/ui/pane-header";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { StatusBadge, type StatusTone } from "@/components/ui/status-badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useRealtimeEvent } from "@/lib/realtime/RealtimeProvider";
import { BulkActionsBar } from "../../../components/common/BulkActionsBar";
import { useRowSelection } from "../../../hooks/useRowSelection";
import { ErrorState } from "../../../components/common/ErrorState";
import { useIndividualOptional } from "../../../contexts/IndividualContext";
import { individualSelectors } from "../../../redux/individual/selectors";
import { PdfPreview } from "../../../components/common/PdfPreview";
import { CitationDocumentOverlay } from "../../../components/common/citations";
import { useUserRoleAndSubscription } from "../../../hooks/useUserRoleAndSubscription";
import { CasePrepUscisFormsAPI } from "../api";
import { FormCitationDocumentsProvider } from "../context/FormCitationDocumentsContext";
import { SourceTypeTag } from "./fieldSource/SourceTypeTag";
import {
  SOURCE_LEGEND_BY_KEY,
  sourceLegendKeyOf,
  type SourceLegendKey
} from "./fieldSource/sourceKinds";
import type { FieldSourceItem, OpenFormCitation } from "./fieldSource/types";
import {
  FieldHoverCardLayer,
  useFieldHoverCard
} from "./formSources/FieldHoverCardLayer";
import { FormCommentsPanel } from "./FormCommentsPanel";
import {
  renderCommentPin,
  useFormCommentsBridge
} from "./formSources/formComments";
import { FormSidePanel } from "./formSources/FormSidePanel";
import { fieldSourceTarget } from "./formSources/openSource";
import { SourcesLegend } from "./formSources/SourcesControls";
import { useFieldSourceUsers } from "../hooks/useFieldSourceUsers";
import { NeedsInputPanel } from "./NeedsInputPanel";
import { useCasePrepUscisForms } from "../context";
import { useCaseIdeOptional } from "../../caseIde/context/CaseIdeContext";
import { useCaseRoute } from "../../caseIde/useCaseRoute";
import { intakePath, submissionPath } from "../../requestsIde/routes";
import { buildFilledFormPath, buildFormsListPath } from "../formRoutes";
import {
  classifyGeneratedPdfLoadFailure,
  hasGeneratedPdf
} from "../generatedPdfLoad";
import { fillingLabel, isNeverFilled } from "../fillProgress";
import { resolveOpenFormTransition } from "../openFormTransition";
import { fieldBoxes, type FormCanvasField } from "../formWidgets";
import { sortIntoReadingOrder } from "../overlayReadingOrder";
import type {
  CasePrepUscisFieldProvenance,
  CasePrepUscisFilledForm,
  CasePrepUscisFormCatalogEntry,
  CasePrepUscisOverlayField,
  CasePrepUscisRunStep,
  CasePrepUscisSource
} from "../types";
import {
  sourceCardLabel,
  sourceDetailLabel,
  sourceStepLeadIn,
  stringifyValue
} from "../utils";
import { AutofillFormDrawer } from "./AutofillFormDrawer";
import {
  FormCanvas,
  type FormCanvasFieldOverlayProps,
  type FormCanvasFieldTarget,
  type FormCanvasHandle
} from "./FormCanvas";
import { FormVersionHistory } from "./FormVersionHistory";
import {
  SearchableSelect,
  SourceBuilderExplainerRail,
  SourceListEditor,
  type SourceListEditorStatus
} from "./SourceListEditor";

type Props = {
  individualId: string;
  caseId: string;
};

type WizardStep =
  | "form"
  | "information"
  | "intake"
  | "sources"
  | "confirmation";
type FormSelectOption = {
  value: string;
  label: string;
  formTitle?: string;
};
type LinkExpiresOption = {
  value: string;
  label: string;
};
const formNumberCollator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base"
});
const linkExpiresOptions: LinkExpiresOption[] = [
  { value: "7", label: "7 Days" },
  { value: "14", label: "14 Days" },
  { value: "30", label: "30 Days" },
  { value: "never", label: "Never" }
];

function typeformStepNumber(step: WizardStep, hideFormStep = false): number {
  // When the form is already chosen (existing-form mode), the "form" step is
  // hidden, so the remaining steps shift down by one.
  const offset = hideFormStep ? 1 : 0;
  if (step === "form") return 1;
  if (step === "information") return 2 - offset;
  return 3 - offset;
}

function typeformStepQuestion(
  step: WizardStep,
  selectedFormNumber: string
): string {
  if (step === "form") return "Which form do you want to fill?";
  if (step === "information") {
    return "Have you collected all of the information required by the form?";
  }
  if (step === "intake") return "Send intake questionnaire?";
  if (step === "confirmation") return "Intake sent";
  return `Which data sources should be used to fill this ${
    selectedFormNumber || "the form"
  }?`;
}

function typeformStepHelper(
  step: WizardStep,
  selectedFormNumber: string
): string {
  if (step === "form") {
    return "Choose from all USCIS, EOIR, or DOL forms.";
  }
  if (step === "information") {
    return "If not, we can help you collect all the information required by the form from the foreign national.";
  }
  if (step === "intake") {
    return `Send an intake questionnaire with the questions required for ${
      selectedFormNumber || "this form"
    }. You will be notified when the foreign national completes it.`;
  }
  if (step === "confirmation") {
    return `You can return here after the intake is completed to fill form ${selectedFormNumber}.`;
  }
  return `Configure the logic for filling this ${
    selectedFormNumber || "form"
  }.`;
}

function compareFormCatalogEntries(
  a: CasePrepUscisFormCatalogEntry,
  b: CasePrepUscisFormCatalogEntry
): number {
  return formNumberCollator.compare(a.form_number, b.form_number);
}

function getFormSelectLabel(form: CasePrepUscisFormCatalogEntry): string {
  const formTitle = form.form_title?.trim();
  return formTitle ? `${form.form_number} - ${formTitle}` : form.form_number;
}

function getExpiresInDays(expiresChoice: string): number {
  if (expiresChoice === "never") return 365;
  return Math.max(1, Math.min(365, Number(expiresChoice || 14)));
}

function formatFormSelectOption(option: FormSelectOption) {
  return (
    <span>
      <span className="font-medium">{option.value}</span>
      {option.formTitle ? ` - ${option.formTitle}` : null}
    </span>
  );
}

function TypeformStepHeader({
  step,
  selectedFormNumber,
  hideFormStep = false
}: {
  step: WizardStep;
  selectedFormNumber: string;
  hideFormStep?: boolean;
}) {
  return (
    <div className="flex items-start gap-3">
      <span className="mt-1 inline-flex h-4.5 min-w-4.5 flex-none items-center justify-center rounded-xs bg-primary px-1 text-sm font-medium text-primary-foreground">
        {typeformStepNumber(step, hideFormStep)}
      </span>
      <div className="min-w-0">
        <h2 className="text-xl font-medium text-fg">
          {typeformStepQuestion(step, selectedFormNumber)}
        </h2>
        <p className="mt-2 text-md text-fg-2">
          {typeformStepHelper(step, selectedFormNumber)}
        </p>
      </div>
    </div>
  );
}

function TypeformOptionCard({
  isSelected,
  isDimmed,
  title,
  description,
  onClick
}: {
  isSelected: boolean;
  isDimmed?: boolean;
  title: string;
  description: string;
  onClick: () => void;
}) {
  const commaIndex = title.indexOf(",");
  const titlePrefix = commaIndex > -1 ? title.slice(0, commaIndex + 1) : "";
  const titleRest =
    commaIndex > -1 ? title.slice(commaIndex + 1).trim() : title;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={isSelected}
      className={cn(
        "flex min-h-45 w-full flex-col justify-center gap-3 rounded-xl p-5 text-left ring-1 transition-colors duration-150 outline-none focus-visible:ring-2 focus-visible:ring-primary/40 lg:size-60",
        isSelected
          ? "bg-accent-soft ring-accent-line"
          : "ring-line-2 hover:bg-fill-1",
        isDimmed && "opacity-60"
      )}
    >
      <span className="text-lg font-medium text-fg lg:min-h-18">
        {titlePrefix ? (
          <>
            <span className="text-accent-seed">{titlePrefix}</span>{" "}
          </>
        ) : null}
        {titleRest}
      </span>
      <span className="text-base text-fg-3">{description}</span>
    </button>
  );
}

function formatDate(value: unknown): string {
  if (!value) return "-";
  const firestoreValue = value as { toDate?: () => Date; seconds?: number };
  const date =
    typeof firestoreValue.toDate === "function"
      ? firestoreValue.toDate()
      : typeof firestoreValue.seconds === "number"
      ? new Date(firestoreValue.seconds * 1000)
      : new Date(String(value));
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
}

function normalizeOverlayComparableValue(value: unknown): string {
  return stringifyValue(value);
}

function overlayValueMapsEqual(
  left: Record<string, unknown>,
  right: Record<string, unknown>
): boolean {
  const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
  for (const key of keys) {
    if (
      normalizeOverlayComparableValue(left[key]) !==
      normalizeOverlayComparableValue(right[key])
    ) {
      return false;
    }
  }
  return true;
}

function changedOverlayValues(
  currentValues: Record<string, unknown>,
  savedValues: Record<string, unknown>
): Record<string, unknown> {
  const changed: Record<string, unknown> = {};
  const keys = new Set([
    ...Object.keys(currentValues),
    ...Object.keys(savedValues)
  ]);
  keys.forEach((key) => {
    if (
      normalizeOverlayComparableValue(currentValues[key]) !==
      normalizeOverlayComparableValue(savedValues[key])
    ) {
      changed[key] = currentValues[key];
    }
  });
  return changed;
}

function overlayValue(
  form: CasePrepUscisFilledForm | null,
  field: CasePrepUscisOverlayField
): unknown {
  const edited = form?.edited_field_values ?? {};
  const base = form?.field_values ?? {};
  return Object.prototype.hasOwnProperty.call(edited, field.field_name)
    ? edited[field.field_name]
    : base[field.field_name] ?? field.value;
}

function storagePathOf(form: CasePrepUscisFilledForm): string | null {
  return form.generated_pdf_storage_path || null;
}

/** A field with an answer on the form; an unchecked box counts only when a person unchecked it. */
function isFilledSourceItem(item: FieldSourceItem): boolean {
  if (item.value === true) return true;
  if (item.value === false) return item.provenance?.kind === "manual_edit";
  return stringifyValue(item.value).trim() !== "";
}

// Time to animate the citation overlay out before it unmounts.
const CITATION_CLOSE_MS = 200;

/** The docked source document a card's View Page opens, shared by the hover card and the list. */
function useCitationOverlay() {
  const [openCitation, setOpenCitation] = useState<OpenFormCitation | null>(
    null
  );
  const [isClosing, setIsClosing] = useState(false);
  const closeTimer = useRef<number | null>(null);

  const close = React.useCallback(() => {
    if (closeTimer.current !== null) return;
    setIsClosing(true);
    closeTimer.current = window.setTimeout(() => {
      setOpenCitation(null);
      setIsClosing(false);
      closeTimer.current = null;
    }, CITATION_CLOSE_MS);
  }, []);

  const toggle = React.useCallback(
    (next: OpenFormCitation) => {
      // Clicking the snippet of the document already open puts it away.
      if (!isClosing && openCitation?.fieldName === next.fieldName) {
        close();
        return;
      }
      if (closeTimer.current !== null) {
        window.clearTimeout(closeTimer.current);
        closeTimer.current = null;
      }
      setIsClosing(false);
      setOpenCitation(next);
    },
    [close, isClosing, openCitation]
  );

  useEffect(
    () => () => {
      if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
    },
    []
  );

  return {
    openCitation,
    isClosing,
    openFieldName: isClosing ? null : openCitation?.fieldName ?? null,
    toggle,
    close
  };
}

function formatDateTimeWithTimezone(value: unknown): string {
  if (!value) return "-";
  const firestoreValue = value as { toDate?: () => Date; seconds?: number };
  const date =
    typeof firestoreValue.toDate === "function"
      ? firestoreValue.toDate()
      : typeof firestoreValue.seconds === "number"
      ? new Date(firestoreValue.seconds * 1000)
      : new Date(String(value));
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short"
  });
}

function firstFieldBox(field: CasePrepUscisOverlayField) {
  return fieldBoxes(field)[0];
}

/** Sorts one page's rows ahead of the next page's (PDF points; far past any page). */
const PAGE_ORDER_STRIDE = 100_000;

function fallbackProvenanceFromRunSteps(
  form: CasePrepUscisFilledForm
): Record<string, CasePrepUscisFieldProvenance> {
  const fallback: Record<string, CasePrepUscisFieldProvenance> = {};
  for (const step of form.run_steps ?? []) {
    for (const [fieldName, value] of Object.entries(step.filled_values ?? {})) {
      if (value != null && !fallback[fieldName]) {
        fallback[fieldName] = {
          kind: "waterfall_source",
          field_name: fieldName,
          value,
          source_type: step.source.source_type,
          source_label: step.source.source_label,
          source_id: step.source.source_id,
          source_free_text: step.source.free_text,
          source_metadata: step.source.metadata,
          step_id: step.step_id,
          step_index: step.index,
          step_status: step.status,
          completed_at: step.completed_at
        };
      }
    }
  }
  return fallback;
}

function runStepStatusIcon(status: string): LucideIcon {
  if (status === "succeeded") return Check;
  if (status === "failed") return X;
  if (status === "running") return Loader;
  if (status === "skipped") return Minus;
  return Clock;
}

function runStepStatusIconClass(status: string): string {
  if (status === "succeeded") return "bg-success-soft text-success";
  if (status === "failed") return "bg-danger-soft text-danger";
  if (status === "running") return "bg-accent-soft text-accent-seed";
  return "bg-fill-2 text-fg-3";
}

function nonNullFilledValueEntries(
  filledValues?: Record<string, unknown> | null
): Array<[string, unknown]> {
  return Object.entries(filledValues ?? {}).filter(
    ([, value]) => value !== null && value !== undefined
  );
}

function newlyFilledValueEntriesForStep(
  steps: CasePrepUscisRunStep[],
  stepIndex: number
): Array<[string, unknown]> {
  const previouslyFilledFieldNames = new Set<string>();
  steps.slice(0, stepIndex).forEach((step) => {
    nonNullFilledValueEntries(step.filled_values).forEach(([key]) => {
      previouslyFilledFieldNames.add(key);
    });
  });

  return nonNullFilledValueEntries(steps[stepIndex]?.filled_values).filter(
    ([key]) => !previouslyFilledFieldNames.has(key)
  );
}

function downloadPdfBlob(blob: Blob, filename: string) {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filename;
  anchor.rel = "noopener noreferrer";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

export const CasePrepUscisFormsPanel = ({ individualId, caseId }: Props) => {
  const navigate = useNavigate();
  const { filledFormId: openFormId } = useParams<{ filledFormId?: string }>();
  const { foreignNationalId } = useCaseRoute();
  const caseRef = useMemo(
    () => ({ individualId, foreignNationalId }),
    [individualId, foreignNationalId]
  );
  const formsListPath = buildFormsListPath(caseRef);
  const { isSuperAdmin, email } = useUserRoleAndSubscription();
  const { forms, catalog, isLoading, error, refetch, deleteForm, upsertForm } =
    useCasePrepUscisForms();
  const [selectedForm, setSelectedForm] =
    useState<CasePrepUscisFilledForm | null>(null);
  const [openingForm, setOpeningForm] =
    useState<CasePrepUscisFilledForm | null>(null);
  const [runHistoryForm, setRunHistoryForm] =
    useState<CasePrepUscisFilledForm | null>(null);
  const [pendingDeleteForm, setPendingDeleteForm] =
    useState<CasePrepUscisFilledForm | null>(null);
  const [isDeletingId, setIsDeletingId] = useState<string | null>(null);
  const openFormTimeoutRef = useRef<number | null>(null);
  const notFoundFormIdRef = useRef<string | null>(null);
  const [isWizardOpen, setIsWizardOpen] = useState(false);

  const sortedForms = useMemo(
    () =>
      [...forms].sort((a, b) =>
        String(b.updated_at ?? b.created_at ?? "").localeCompare(
          String(a.updated_at ?? a.created_at ?? "")
        )
      ),
    [forms]
  );
  const canViewRunHistory =
    isSuperAdmin &&
    String(email || "")
      .toLowerCase()
      .endsWith("@caseblink.com");

  const handleDelete = async (form: CasePrepUscisFilledForm) => {
    setIsDeletingId(form.case_extend_uscis_filled_form_id);
    try {
      await deleteForm(form.case_extend_uscis_filled_form_id);
      toast.success("Form deleted");
    } catch (err) {
      toast.error("Unable to delete form", {
        description: err instanceof Error ? err.message : undefined
      });
    } finally {
      setIsDeletingId(null);
    }
  };

  const formIds = useMemo(
    () => sortedForms.map((f) => f.case_extend_uscis_filled_form_id),
    [sortedForms]
  );
  const {
    selectedIds: selectedFormIds,
    count: selectedFormCount,
    isSelected: isFormSelected,
    toggle: toggleForm,
    toggleAll: toggleAllForms,
    clear: clearFormSelection,
    allSelected: allFormsSelected
  } = useRowSelection(formIds);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const handleBulkDeleteForms = async () => {
    if (selectedFormIds.length === 0) return;
    setIsBulkDeleting(true);
    try {
      await Promise.all(selectedFormIds.map((id) => deleteForm(id)));
      toast.success(
        selectedFormIds.length === 1
          ? "Form deleted"
          : `${selectedFormIds.length} forms deleted`
      );
      clearFormSelection();
    } catch (err) {
      toast.error("Unable to delete forms", {
        description: err instanceof Error ? err.message : undefined
      });
    } finally {
      setIsBulkDeleting(false);
      setBulkDeleteOpen(false);
    }
  };

  // The URL owns which form is open, so opening one is only a navigation. The
  // effect below turns the new id back into the detail view.
  const openFilledForm = (form: CasePrepUscisFilledForm) => {
    navigate(
      buildFilledFormPath(caseRef, form.case_extend_uscis_filled_form_id)
    );
  };

  useEffect(
    () => () => {
      if (openFormTimeoutRef.current !== null) {
        window.clearTimeout(openFormTimeoutRef.current);
      }
    },
    []
  );

  // Apply whatever the id in the URL calls for. This runs for clicks, deep links,
  // and Back/Forward alike, so every route into the detail view reveals the form
  // the same way.
  useEffect(() => {
    const clearPendingOpen = () => {
      if (openFormTimeoutRef.current !== null) {
        window.clearTimeout(openFormTimeoutRef.current);
        openFormTimeoutRef.current = null;
      }
    };

    const transition = resolveOpenFormTransition({
      openFormId,
      forms,
      selectedForm,
      openingForm,
      isLoading,
      hasError: Boolean(error)
    });

    if (transition.kind === "wait") return;

    if (transition.kind === "close") {
      clearPendingOpen();
      setSelectedForm(null);
      setOpeningForm(null);
      return;
    }

    if (transition.kind === "refresh") {
      setSelectedForm(transition.form);
      return;
    }

    if (transition.kind === "missing") {
      if (openFormId && notFoundFormIdRef.current !== openFormId) {
        notFoundFormIdRef.current = openFormId;
        toast.error("Form not found");
        navigate(formsListPath, { replace: true });
      }
      return;
    }

    // Any timer still pending belongs to a form we have since navigated away from.
    clearPendingOpen();
    const { form } = transition;
    setOpeningForm(form);
    openFormTimeoutRef.current = window.setTimeout(() => {
      setSelectedForm(form);
      setOpeningForm(null);
      openFormTimeoutRef.current = null;
    }, 260);
  }, [
    error,
    forms,
    formsListPath,
    isLoading,
    navigate,
    openFormId,
    openingForm,
    selectedForm
  ]);

  return (
    <div className="flex h-full max-h-full min-h-0 w-full flex-col overflow-hidden bg-surface">
      {selectedForm || openingForm ? null : (
        <PaneHeader
          title="Forms"
          count={isLoading ? null : sortedForms.length}
          className="pr-2"
          actions={
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                aria-label="Refresh forms"
                onClick={() => {
                  refetch().catch(() => undefined);
                }}
              >
                <RefreshCw />
              </Button>
              <Button
                type="button"
                variant="primary"
                size="xs"
                onClick={() => setIsWizardOpen(true)}
              >
                <Plus />
                Fill New Form
              </Button>
            </div>
          }
        />
      )}

      <div className="relative flex min-h-0 flex-1 flex-col overflow-auto">
        {selectedForm ? (
          <FilledFormDetail
            individualId={individualId}
            form={selectedForm}
            onBack={() => navigate(formsListPath)}
            onUpdated={(form) => {
              upsertForm(form);
              setSelectedForm(form);
            }}
          />
        ) : openingForm ? (
          <OpeningFilledFormPreview />
        ) : error && isTransientError(error) && sortedForms.length === 0 ? (
          <ReconnectingState onRetry={() => refetch().catch(() => undefined)} />
        ) : error ? (
          <div className="flex w-full flex-1 items-center justify-center px-6 py-10">
            <ErrorState
              title="Couldn’t load forms"
              message={error.message}
              buttonCta="Retry"
              onRetry={() => {
                refetch().catch(() => undefined);
              }}
            />
          </div>
        ) : sortedForms.length === 0 && !isLoading ? (
          <EmptyState
            icon={FilePlus}
            title="No Forms Found"
            description="Fill and save USCIS, EOIR, and DOL forms."
          />
        ) : (
          <>
            <BulkActionsBar
              count={selectedFormCount}
              onDelete={() => setBulkDeleteOpen(true)}
              onClear={clearFormSelection}
              isDeleting={isBulkDeleting}
              itemNoun="form"
            />
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={allFormsSelected}
                      onCheckedChange={toggleAllForms}
                      aria-label="Select all forms"
                    />
                  </TableHead>
                  <TableHead>Form</TableHead>
                  <TableHead>Generated</TableHead>
                  <TableHead>Last Updated</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading
                  ? Array.from({ length: 6 }).map((_, idx) => (
                      <TableRow key={`case-prep-uscis-skeleton-${idx}`}>
                        <TableCell className="w-10">
                          <Skeleton className="size-4 rounded-xs" />
                        </TableCell>
                        <TableCell>
                          <div className="flex w-full flex-col items-start gap-1">
                            <Skeleton className="h-3 w-44" />
                            <Skeleton className="h-3 w-28" />
                          </div>
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-3 w-24" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-3 w-24" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-3 w-20" />
                        </TableCell>
                      </TableRow>
                    ))
                  : sortedForms.map((form) => (
                      <TableRow
                        key={form.case_extend_uscis_filled_form_id}
                        className="h-11 cursor-pointer"
                        data-state={
                          isFormSelected(form.case_extend_uscis_filled_form_id)
                            ? "selected"
                            : undefined
                        }
                        onClick={() => openFilledForm(form)}
                      >
                        <TableCell
                          className="w-10"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <Checkbox
                            checked={isFormSelected(
                              form.case_extend_uscis_filled_form_id
                            )}
                            onCheckedChange={() =>
                              toggleForm(form.case_extend_uscis_filled_form_id)
                            }
                            aria-label={`Select ${form.form_number || "form"}`}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col items-start">
                            <span className="whitespace-nowrap font-medium text-fg">
                              {form.form_number}
                            </span>
                            <span className="whitespace-nowrap text-sm text-fg-3">
                              {form.form_title || "USCIS form"}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="whitespace-nowrap text-sm text-fg-3">
                            {formatDate(form.generated_at)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="whitespace-nowrap text-sm text-fg-3">
                            {formatDate(form.updated_at ?? form.created_at)}
                          </span>
                        </TableCell>
                        <TableCell onClick={(event) => event.stopPropagation()}>
                          <div className="flex justify-end">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon-xs"
                                  aria-label={`Actions for ${
                                    form.form_number || "USCIS form"
                                  }`}
                                  disabled={isDeletingId !== null}
                                >
                                  {isDeletingId ===
                                  form.case_extend_uscis_filled_form_id ? (
                                    <Spinner className="size-3.5" />
                                  ) : (
                                    <MoreHorizontal />
                                  )}
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {canViewRunHistory ? (
                                  <DropdownMenuItem
                                    onSelect={() => setRunHistoryForm(form)}
                                  >
                                    <History />
                                    View Run
                                  </DropdownMenuItem>
                                ) : null}
                                <DropdownMenuItem
                                  variant="destructive"
                                  disabled={isDeletingId !== null}
                                  onSelect={() => {
                                    if (isDeletingId !== null) return;
                                    setPendingDeleteForm(form);
                                  }}
                                >
                                  <Trash2 />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
          </>
        )}
      </div>

      <FillFormWizard
        isOpen={isWizardOpen}
        onClose={() => setIsWizardOpen(false)}
        individualId={individualId}
        caseId={caseId}
        catalog={catalog}
        onCreated={(form) => {
          upsertForm(form);
          openFilledForm(form);
        }}
      />

      <AlertDialog
        open={Boolean(pendingDeleteForm)}
        onOpenChange={(nextOpen) => {
          if (nextOpen || isDeletingId !== null) return;
          setPendingDeleteForm(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Filled Form?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{" "}
              <strong className="font-medium text-fg">
                {pendingDeleteForm?.form_number || "this filled form"}
              </strong>
              ? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingId !== null}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              variant="danger"
              disabled={!pendingDeleteForm || isDeletingId !== null}
              onClick={() => {
                if (!pendingDeleteForm || isDeletingId !== null) return;
                const formToDelete = pendingDeleteForm;
                setPendingDeleteForm(null);
                handleDelete(formToDelete);
              }}
            >
              {isDeletingId !== null ? <Spinner className="size-3.5" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={bulkDeleteOpen}
        onOpenChange={(nextOpen) => {
          if (nextOpen || isBulkDeleting) return;
          setBulkDeleteOpen(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Forms?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{" "}
              <strong className="font-medium text-fg">
                {selectedFormCount} {selectedFormCount === 1 ? "form" : "forms"}
              </strong>
              ? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isBulkDeleting}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              variant="danger"
              disabled={isBulkDeleting || selectedFormCount === 0}
              onClick={(event) => {
                // The dialog stays open, showing progress, until the deletes settle.
                event.preventDefault();
                handleBulkDeleteForms();
              }}
            >
              {isBulkDeleting ? <Spinner className="size-3.5" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <RunHistoryModal
        individualId={individualId}
        form={runHistoryForm}
        onClose={() => setRunHistoryForm(null)}
      />
    </div>
  );
};

function FillFormWizard({
  isOpen,
  onClose,
  individualId,
  caseId,
  catalog = [],
  onCreated,
  existingForm,
  initialStep = "form"
}: {
  isOpen: boolean;
  onClose: () => void;
  individualId: string;
  caseId: string;
  catalog?: CasePrepUscisFormCatalogEntry[];
  onCreated: (form: CasePrepUscisFilledForm) => void;
  // When provided, the wizard runs against this already-existing form (the
  // "form" step is skipped) instead of creating a new one.
  existingForm?: CasePrepUscisFilledForm;
  initialStep?: WizardStep;
}) {
  const fieldId = useId();
  const individualContext = useIndividualOptional();
  const reduxIndividualEmail =
    useSelector(individualSelectors.selectEmail) ?? "";
  const defaultRecipientEmail =
    individualContext?.individual?.email?.trim() || reduxIndividualEmail;
  const recipientFullName = useMemo(() => {
    const individual = individualContext?.individual;
    const fullName = [
      individual?.firstName,
      individual?.middleName,
      individual?.lastName
    ]
      .map((part) => (part ?? "").trim())
      .filter(Boolean)
      .join(" ");
    return fullName || "the foreign national";
  }, [
    individualContext?.individual?.firstName,
    individualContext?.individual?.lastName,
    individualContext?.individual?.middleName
  ]);
  const [step, setStep] = useState<WizardStep>("form");
  const [selectedFormNumber, setSelectedFormNumber] = useState("");
  const [hasInfo, setHasInfo] = useState<"yes" | "no" | "manual" | null>(null);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [customMessage, setCustomMessage] = useState("");
  const [expiresChoice, setExpiresChoice] = useState("14");
  const [sources, setSources] = useState<CasePrepUscisSource[]>([]);
  const [sourceStatus, setSourceStatus] = useState<SourceListEditorStatus>({
    isLoadingSources: false,
    isRecommendingSources: false,
    hasOpenDraft: false
  });
  const [isSendingIntake, setIsSendingIntake] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [isStartingBlank, setIsStartingBlank] = useState(false);
  const [isBuilderExplainerVisible, setIsBuilderExplainerVisible] =
    useState(true);
  // Recommendations are a one-shot prefill per form, not a re-fetch on every
  // visit to the sources step, so the attorney's edits are never overwritten.
  const recommendedForFormRef = useRef("");

  const formOptions = useMemo<FormSelectOption[]>(
    () =>
      [...catalog].sort(compareFormCatalogEntries).map((form) => ({
        value: form.form_number,
        label: getFormSelectLabel(form),
        formTitle: form.form_title?.trim() || undefined
      })),
    [catalog]
  );
  const selectedFormOption =
    formOptions.find((option) => option.value === selectedFormNumber) ?? null;

  // Read the latest existing form without re-seeding on every reference change:
  // a background list refresh must not wipe the attorney's in-progress edits.
  const existingFormRef = useRef(existingForm);
  existingFormRef.current = existingForm;
  const existingFormId = existingForm?.case_extend_uscis_filled_form_id;

  useEffect(() => {
    if (!isOpen) {
      setStep("form");
      setSelectedFormNumber("");
      setHasInfo(null);
      setRecipientEmail("");
      setCustomMessage("");
      setExpiresChoice("14");
      setSources([]);
      setIsBuilderExplainerVisible(true);
      recommendedForFormRef.current = "";
      return;
    }
    // Existing-form mode enters partway through with the form already chosen,
    // seeding its saved sources so the sources step reflects prior edits. Keyed
    // on the form id, so it seeds on open only, not on later data refreshes.
    const seedForm = existingFormRef.current;
    if (seedForm) {
      setStep(initialStep);
      setSelectedFormNumber(seedForm.form_number);
      setSources(seedForm.source_steps ?? []);
    }
    setRecipientEmail((defaultRecipientEmail || "").trim());
    setCustomMessage("");
    setExpiresChoice("14");
  }, [defaultRecipientEmail, existingFormId, initialStep, isOpen]);

  const sendIntake = async () => {
    const recipient = recipientEmail.trim();
    if (!selectedFormNumber || !recipient || isSendingIntake) {
      return;
    }
    setIsSendingIntake(true);
    try {
      const payload = {
        case_id: caseId,
        form_number: selectedFormNumber,
        recipient_email: recipient,
        expires_in_days: getExpiresInDays(expiresChoice),
        attorney_note: customMessage.trim() || undefined
      };
      await CasePrepUscisFormsAPI.sendIntake(individualId, payload);
      setStep("confirmation");
    } catch (err) {
      toast.error("Unable to send intake", {
        description: err instanceof Error ? err.message : undefined
      });
    } finally {
      setIsSendingIntake(false);
    }
  };

  const runForm = async () => {
    if (!selectedFormNumber || sources.length === 0 || isRunning) return;
    setIsRunning(true);
    try {
      // Existing-form mode fills the already-created record in place so it does
      // not create a duplicate form for the case.
      const form = existingForm
        ? await CasePrepUscisFormsAPI.runFilledForm(
            individualId,
            existingForm.case_extend_uscis_filled_form_id,
            {
              source_steps: sources,
              flatten_pdf: false,
              overwrite_existing_values: false
            }
          )
        : await CasePrepUscisFormsAPI.createAndRun(individualId, {
            case_id: caseId,
            form_number: selectedFormNumber,
            source_steps: sources,
            run_immediately: true,
            flatten_pdf: false
          });
      onCreated(form);
      if (form.status === "generating") {
        toast(`Filling ${form.form_number}`, {
          description:
            "This takes a few minutes. Progress shows on the form; you can keep working."
        });
      } else if (form.status === "failed") {
        toast.warning("Run finished without a generated PDF");
      } else {
        toast.success("Form generated");
      }
      // In existing-form mode we are already on this form's detail; letting the
      // updated form render in place keeps the just-filled PDF on screen rather
      // than navigating away.
      if (!existingForm) onClose();
    } catch (err) {
      toast.error("Unable to fill form", {
        description: err instanceof Error ? err.message : undefined
      });
    } finally {
      setIsRunning(false);
    }
  };

  const startBlankForm = async () => {
    if (!selectedFormNumber || isStartingBlank || isRunning) return;
    setIsStartingBlank(true);
    try {
      // Existing-form mode initializes the blank PDF on the current record
      // rather than creating a new one.
      const form = existingForm
        ? await CasePrepUscisFormsAPI.generateFilledForm(
            individualId,
            existingForm.case_extend_uscis_filled_form_id,
            { initialize_blank: true }
          )
        : await CasePrepUscisFormsAPI.createAndRun(individualId, {
            case_id: caseId,
            form_number: selectedFormNumber,
            source_steps: [],
            run_immediately: false,
            initialize_blank: true,
            flatten_pdf: false
          });
      onCreated(form);
      if (form.status === "failed") {
        toast.warning("Could not prepare a blank form");
      } else {
        toast.success(`Blank ${selectedFormNumber} ready to fill`);
      }
      // Existing-form mode stays on the detail so the blank PDF shows in place.
      if (!existingForm) onClose();
    } catch (err) {
      toast.error("Unable to start a blank form", {
        description: err instanceof Error ? err.message : undefined
      });
    } finally {
      setIsStartingBlank(false);
    }
  };

  const handlePrimaryAction = () => {
    if (step === "form") {
      setStep("information");
      return;
    }
    if (step === "information") {
      if (hasInfo === "no") {
        setStep("intake");
        return;
      }
      if (hasInfo === "manual") {
        startBlankForm();
        return;
      }
      setStep("sources");
      return;
    }
    if (step === "intake") {
      sendIntake();
      return;
    }
    if (step === "sources") {
      runForm();
      return;
    }
    onClose();
  };
  const primaryButtonLabel =
    step === "intake"
      ? "Send"
      : step === "sources"
      ? `Fill ${selectedFormNumber || "Form"}`
      : step === "information" && hasInfo === "manual"
      ? isStartingBlank
        ? "Preparing..."
        : "Start Blank Form"
      : "OK";
  const primaryButtonHint = "Press Enter ↵";
  const isPrimaryDisabled =
    (step === "form" && !selectedFormNumber) ||
    (step === "information" && (hasInfo === null || isStartingBlank)) ||
    (step === "intake" && (!recipientEmail.trim() || isSendingIntake)) ||
    (step === "sources" &&
      (isRunning ||
        isStartingBlank ||
        sourceStatus.isRecommendingSources ||
        sources.length === 0 ||
        sourceStatus.hasOpenDraft));
  const actionControls =
    step === "confirmation" ||
    step === "intake" ||
    step === "sources" ? null : (
      <div className="mt-6 flex items-center gap-3">
        <Button
          type="button"
          onClick={handlePrimaryAction}
          variant="primary"
          size="md"
          disabled={isPrimaryDisabled}
          className="min-w-14"
        >
          {primaryButtonLabel}
        </Button>
        <span className="hidden text-sm text-fg-3 md:block">
          {primaryButtonHint}
        </span>
      </div>
    );

  const isCloseAction =
    step === "form" ||
    step === "confirmation" ||
    Boolean(existingForm && step === "information");

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <SheetContent
        side="right"
        aria-describedby={undefined}
        className={cn(
          "gap-0 overflow-hidden p-0 transition-[max-width] duration-200 ease-out data-[side=right]:w-full",
          step === "sources"
            ? "data-[side=right]:sm:max-w-305"
            : step === "information"
            ? "data-[side=right]:sm:max-w-235"
            : "data-[side=right]:sm:max-w-190"
        )}
        onKeyDown={(event) => {
          const target = event.target as HTMLElement | null;
          if (
            event.key !== "Enter" ||
            event.defaultPrevented ||
            event.shiftKey ||
            target?.tagName === "TEXTAREA" ||
            (step === "sources" && sourceStatus.hasOpenDraft) ||
            isPrimaryDisabled ||
            step === "intake" ||
            step === "confirmation"
          ) {
            return;
          }
          event.preventDefault();
          handlePrimaryAction();
        }}
      >
        <SheetTitle className="sr-only">Fill Form</SheetTitle>
        <div className="relative flex h-full min-h-0 flex-col">
          <div
            className={cn(
              "relative flex min-h-0 flex-1 overflow-x-hidden overflow-y-auto",
              step === "sources"
                ? "items-stretch justify-stretch"
                : "items-center justify-center px-5 py-6 md:px-10 md:py-9"
            )}
          >
            <div
              className={cn(
                "w-full animate-in fade-in-0 slide-in-from-bottom-3 duration-200 ease-out",
                step === "sources"
                  ? "flex h-full min-h-0 flex-col"
                  : step === "information"
                  ? "max-w-200"
                  : "max-w-155"
              )}
            >
              {step === "sources" ? null : (
                <TypeformStepHeader
                  step={step}
                  selectedFormNumber={selectedFormNumber}
                  hideFormStep={Boolean(existingForm)}
                />
              )}

              <div
                className={
                  step === "sources"
                    ? "flex min-h-0 w-full flex-1 flex-col items-center overflow-x-visible overflow-y-auto"
                    : "mt-5 md:ml-7.5"
                }
              >
                {step === "form" ? (
                  <div className="flex flex-col gap-5">
                    <div className="max-w-160">
                      <SearchableSelect<FormSelectOption>
                        options={formOptions}
                        value={selectedFormOption}
                        placeholder="Select a form"
                        searchPlaceholder="Search forms…"
                        formatOptionLabel={formatFormSelectOption}
                        className="h-9 text-md"
                        onChange={(option) =>
                          setSelectedFormNumber(option?.value ?? "")
                        }
                      />
                    </div>
                    {actionControls}
                  </div>
                ) : step === "information" ? (
                  <div className="flex flex-col gap-4">
                    <div className="flex max-w-200 flex-col gap-4 lg:flex-row">
                      <TypeformOptionCard
                        isSelected={hasInfo === "yes"}
                        isDimmed={hasInfo !== null && hasInfo !== "yes"}
                        title="Yes, I have the information to fill the form."
                        description="Let's fill the form."
                        onClick={() => setHasInfo("yes")}
                      />
                      <TypeformOptionCard
                        isSelected={hasInfo === "no"}
                        isDimmed={hasInfo !== null && hasInfo !== "no"}
                        title="No, I don't have all of the information to fill the form."
                        description="Let's collect the information first."
                        onClick={() => setHasInfo("no")}
                      />
                      <TypeformOptionCard
                        isSelected={hasInfo === "manual"}
                        isDimmed={hasInfo !== null && hasInfo !== "manual"}
                        title="I'll fill it in myself."
                        description="Skip autofill and start from a blank form you can type into."
                        onClick={() => setHasInfo("manual")}
                      />
                    </div>
                    {actionControls}
                  </div>
                ) : step === "intake" ? (
                  <div className="flex max-w-160 flex-col gap-5">
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor={`${fieldId}-recipient`}>
                        Recipient Email*
                      </Label>
                      <Input
                        id={`${fieldId}-recipient`}
                        type="email"
                        value={recipientEmail}
                        placeholder="No email on profile"
                        disabled={true}
                        readOnly={true}
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor={`${fieldId}-expires`}>
                        Link Expires*
                      </Label>
                      <Select
                        value={expiresChoice}
                        disabled={isSendingIntake}
                        onValueChange={(value) =>
                          setExpiresChoice(value || "14")
                        }
                      >
                        <SelectTrigger
                          id={`${fieldId}-expires`}
                          className="w-full min-w-62.5"
                        >
                          <SelectValue placeholder="14 Days" />
                        </SelectTrigger>
                        <SelectContent>
                          {linkExpiresOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor={`${fieldId}-message`}>
                        {`Write a Message to ${recipientFullName}`}
                      </Label>
                      <Textarea
                        id={`${fieldId}-message`}
                        className="min-h-40"
                        value={customMessage}
                        placeholder="Add a personal note for your client..."
                        disabled={isSendingIntake}
                        onChange={(event) =>
                          setCustomMessage(event.target.value)
                        }
                      />
                    </div>
                    {actionControls}
                  </div>
                ) : step === "confirmation" ? (
                  <div className="flex w-full min-w-0 items-start gap-3 rounded-xl p-4 text-left ring-1 ring-line-2">
                    <span className="flex size-8 flex-none items-center justify-center rounded-full bg-success-soft text-success">
                      <Check className="size-4" />
                    </span>
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                      <span className="text-base font-medium text-fg">
                        Intake Questionnaire Sent
                      </span>
                      <span className="text-sm text-fg-3">
                        You will be notified when the foreign national completes
                        the intake. Return here then to fill form{" "}
                        {selectedFormNumber}.
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="flex h-full min-h-0 w-full flex-col gap-3">
                    <div className="flex h-full min-h-0 w-full flex-col items-stretch justify-center gap-5 lg:flex-row lg:gap-0">
                      <div
                        aria-disabled={isRunning}
                        className={cn(
                          "relative z-1 flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-visible bg-surface px-5 py-6 transition duration-150 md:px-10 md:py-9 lg:px-12",
                          isRunning &&
                            "pointer-events-none select-none opacity-45 grayscale"
                        )}
                      >
                        <SourceListEditor
                          individualId={individualId}
                          caseId={caseId}
                          formNumber={selectedFormNumber}
                          sources={sources}
                          onChange={setSources}
                          disabled={isRunning || isStartingBlank}
                          recommend={
                            sources.length === 0 &&
                            recommendedForFormRef.current !== selectedFormNumber
                          }
                          onRecommendationsRequested={() => {
                            recommendedForFormRef.current = selectedFormNumber;
                          }}
                          onStatusChange={setSourceStatus}
                          header={
                            <TypeformStepHeader
                              step={step}
                              selectedFormNumber={selectedFormNumber}
                              hideFormStep={Boolean(existingForm)}
                            />
                          }
                        />
                      </div>
                      {isBuilderExplainerVisible ? (
                        <SourceBuilderExplainerRail
                          onDismiss={() => setIsBuilderExplainerVisible(false)}
                        />
                      ) : null}
                    </div>
                    {actionControls}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="relative flex flex-none items-center justify-between border-t border-line-2 px-4 py-3">
            <Button
              type="button"
              variant="secondary"
              size="md"
              onClick={() => {
                if (step === "form") onClose();
                // In existing-form mode information is the entry step, so there
                // is no form step to return to.
                else if (step === "information") {
                  if (existingForm) onClose();
                  else setStep("form");
                } else if (step === "intake") setStep("information");
                else if (step === "sources") setStep("information");
                else onClose();
              }}
            >
              {isCloseAction ? <X /> : <ArrowLeft />}
              {isCloseAction ? "Close" : "Back"}
            </Button>
            {step === "intake" || step === "sources" ? (
              <div className="flex items-center gap-3">
                {step === "sources" ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="md"
                    onClick={startBlankForm}
                    disabled={isRunning || isStartingBlank}
                  >
                    {isStartingBlank ? <Spinner className="size-3.5" /> : null}
                    Skip Autofill, Start Blank
                  </Button>
                ) : null}
                <Button
                  type="button"
                  onClick={handlePrimaryAction}
                  variant="primary"
                  size="md"
                  disabled={isPrimaryDisabled}
                  className="min-w-20"
                >
                  {(step === "intake" ? isSendingIntake : isRunning) ? (
                    <Spinner className="size-3.5" />
                  ) : step === "intake" ? (
                    <Send />
                  ) : null}
                  {primaryButtonLabel}
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function OpeningFilledFormPreview() {
  return (
    <div className="flex h-full min-h-0 w-full items-center justify-center bg-surface">
      <BrandLoader label="Loading form" />
    </div>
  );
}

function FormStatusBadge({ status }: { status: string }) {
  const badges: Record<string, { tone: StatusTone; label: string }> = {
    not_started: { tone: "neutral", label: "Not Started" },
    draft: { tone: "neutral", label: "Blank" },
    generating: { tone: "accent", label: "Filling" },
    failed: { tone: "danger", label: "Failed" }
  };
  const badge = badges[status];
  if (!badge) return null;
  return <StatusBadge tone={badge.tone}>{badge.label}</StatusBadge>;
}

export function FilledFormDetail({
  individualId,
  form,
  onBack,
  onUpdated,
  moreMenuItems
}: {
  individualId: string;
  form: CasePrepUscisFilledForm;
  onBack: () => void;
  onUpdated: (form: CasePrepUscisFilledForm) => void;
  /** Extra items for the overflow menu (the Case IDE's Add to Case Files…). */
  moreMenuItems?: React.ReactNode;
}) {
  const [generatedPdf, setGeneratedPdf] = useState<File | null>(null);
  const [isGeneratedPdfLoading, setIsGeneratedPdfLoading] = useState(false);
  const [generatedPdfError, setGeneratedPdfError] = useState("");
  /** The record advertises a PDF but the server has none left to serve. */
  const [generatedPdfGone, setGeneratedPdfGone] = useState(false);
  const [editedValues, setEditedValues] = useState<Record<string, unknown>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isInitializingBlank, setIsInitializingBlank] = useState(false);
  const [isAutofillOpen, setIsAutofillOpen] = useState(false);
  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [selectedFieldName, setSelectedFieldName] = useState<string | null>(
    null
  );
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);
  const [isolatedKind, setIsolatedKind] = useState<SourceLegendKey | null>(
    null
  );
  /** Set for a few seconds after a manual save lands. */
  const [justSaved, setJustSaved] = useState(false);
  const canvasHostRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<FormCanvasHandle>(null);
  /** Edit Value focuses the field itself; that focus must not reopen the card. */
  const editingFieldRef = useRef<string | null>(null);
  /** The stored PDF our own last save wrote; already on screen, so not reloaded. */
  const savedStoragePathRef = useRef<string | null>(null);
  const editedValuesRef = useRef<Record<string, unknown>>({});
  const carryOverRef = useRef<Record<string, unknown>>({});
  const onUpdatedRef = useRef(onUpdated);
  const refreshFillRef = useRef<(() => void) | null>(null);
  const [dismissedRunError, setDismissedRunError] = useState<string | null>(
    null
  );
  const caseIde = useCaseIdeOptional();
  const navigate = useNavigate();
  const filledFormId = form.case_extend_uscis_filled_form_id;
  const overlayFields = form.overlay_fields ?? [];
  const savedOverlayValues = useMemo(() => {
    const next: Record<string, unknown> = {};
    for (const field of overlayFields) {
      next[field.field_name] = overlayValue(form, field);
    }
    return next;
  }, [form, overlayFields]);
  const hasUnsavedChanges = useMemo(
    () => !overlayValueMapsEqual(editedValues, savedOverlayValues),
    [editedValues, savedOverlayValues]
  );
  const lastSavedAt = form.updated_at ?? form.generated_at ?? form.created_at;
  const provenanceItems = useMemo<FieldSourceItem[]>(() => {
    const fallbackProvenance = fallbackProvenanceFromRunSteps(form);
    const storedProvenance = form.field_provenance ?? {};
    const overlayByField = new Map(
      overlayFields.map((field) => [field.field_name, field])
    );
    const fieldNames = new Set<string>([
      ...Object.keys(savedOverlayValues),
      ...Object.keys(storedProvenance),
      ...Object.keys(fallbackProvenance)
    ]);

    const items = Array.from(fieldNames)
      .map((fieldName) => {
        const overlayField = overlayByField.get(fieldName);
        const box = overlayField ? firstFieldBox(overlayField) : undefined;
        const provenance =
          storedProvenance[fieldName] ?? fallbackProvenance[fieldName];
        const sourceType = String(provenance?.source_type || "");
        const matchingRunStep =
          sourceType === "free_text" && !provenance?.source_free_text
            ? (form.run_steps ?? []).find(
                (step) =>
                  step.step_id === provenance?.step_id ||
                  step.index === provenance?.step_index
              )
            : null;
        const displayProvenance =
          matchingRunStep?.source.free_text && provenance
            ? {
                ...provenance,
                source_free_text: matchingRunStep.source.free_text
              }
            : provenance;
        const item: FieldSourceItem = {
          fieldName,
          question: overlayField?.question || "",
          pdfLabel: overlayField?.pdf_label ?? null,
          formText: overlayField?.form_text ?? null,
          fieldType: String(overlayField?.field_type || "Field"),
          value: Object.prototype.hasOwnProperty.call(
            savedOverlayValues,
            fieldName
          )
            ? savedOverlayValues[fieldName]
            : displayProvenance?.value,
          pageNumber: box ? box.pageIndex + 1 : undefined,
          provenance: displayProvenance
        };
        return item;
      })
      .filter((item) => isFilledSourceItem(item));
    const boxFor = (item: FieldSourceItem) => {
      const overlayField = overlayByField.get(item.fieldName);
      return overlayField ? firstFieldBox(overlayField) : undefined;
    };
    const sortKey = (item: FieldSourceItem) =>
      item.pdfLabel || item.question || item.fieldName;
    const byLabel = (a: FieldSourceItem, b: FieldSourceItem) =>
      sortKey(a).localeCompare(sortKey(b));
    const placed = items.filter((item) => boxFor(item));
    // Fields with no geometry follow, by label.
    const unplaced = items.filter((item) => !boxFor(item)).sort(byLabel);
    return [
      ...sortIntoReadingOrder([...placed].sort(byLabel), (item) => {
        const box = boxFor(item);
        return {
          top: (box?.pageIndex ?? 0) * PAGE_ORDER_STRIDE + (box?.top ?? 0),
          left: box?.left ?? 0
        };
      }),
      ...unplaced
    ];
  }, [form, overlayFields, savedOverlayValues]);

  useEffect(() => {
    // Typing that landed while a save was in flight is kept on top.
    setEditedValues({ ...savedOverlayValues, ...carryOverRef.current });
    carryOverRef.current = {};
  }, [savedOverlayValues]);

  useEffect(() => {
    editedValuesRef.current = editedValues;
  }, [editedValues]);

  useEffect(() => {
    if (!justSaved) return undefined;
    if (hasUnsavedChanges) {
      setJustSaved(false);
      return undefined;
    }
    const handle = window.setTimeout(() => setJustSaved(false), 4000);
    return () => window.clearTimeout(handle);
  }, [hasUnsavedChanges, justSaved]);

  useEffect(() => {
    onUpdatedRef.current = onUpdated;
  }, [onUpdated]);

  useEffect(() => {
    if (!filledFormId || form.status !== "generating") {
      return undefined;
    }

    let isMounted = true;
    const refreshGeneratingForm = () => {
      CasePrepUscisFormsAPI.getFilledForm(individualId, filledFormId)
        .then((updated) => {
          if (isMounted) onUpdatedRef.current(updated);
        })
        .catch(() => undefined);
    };

    refreshGeneratingForm();
    refreshFillRef.current = refreshGeneratingForm;
    // The case stream's form.progress refreshes at once; this is the fallback.
    const handle = window.setInterval(refreshGeneratingForm, 10000);
    return () => {
      isMounted = false;
      refreshFillRef.current = null;
      window.clearInterval(handle);
    };
  }, [filledFormId, form.status, individualId]);

  useRealtimeEvent<{ filled_form_id?: string }>("form.progress", (data) => {
    if (data.filled_form_id === filledFormId) refreshFillRef.current?.();
  });

  const storedPdfPath = storagePathOf(form);
  const pdfExpected = hasGeneratedPdf(form);
  useEffect(() => {
    if (!filledFormId || form.status === "generating" || !pdfExpected) {
      setGeneratedPdf(null);
      setIsGeneratedPdfLoading(false);
      setGeneratedPdfError("");
      setGeneratedPdfGone(false);
      return undefined;
    }
    // Our own save: the editor already shows exactly these bytes.
    if (storedPdfPath && storedPdfPath === savedStoragePathRef.current) {
      return undefined;
    }
    savedStoragePathRef.current = null;

    let isMounted = true;
    setGeneratedPdf(null);
    setGeneratedPdfError("");
    setGeneratedPdfGone(false);
    setIsGeneratedPdfLoading(true);
    CasePrepUscisFormsAPI.getFilledFormPdf(individualId, filledFormId)
      .then((blob) => {
        if (!isMounted) return;
        setGeneratedPdf(
          new File([blob], `${form.form_number || "filled-form"}.pdf`, {
            type: blob.type || "application/pdf"
          })
        );
      })
      .catch((error) => {
        if (!isMounted) return;
        const failure = classifyGeneratedPdfLoadFailure(error);
        if (failure.kind === "gone") setGeneratedPdfGone(true);
        else setGeneratedPdfError(failure.message);
      })
      .finally(() => {
        if (!isMounted) return;
        setIsGeneratedPdfLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [
    filledFormId,
    form.form_number,
    form.generated_pdf_file_id,
    form.generated_pdf_url,
    form.status,
    individualId,
    pdfExpected,
    storedPdfPath
  ]);

  const saveChanges = async () => {
    if (!hasUnsavedChanges || isSaving) return;
    const changedValues = changedOverlayValues(
      editedValues,
      savedOverlayValues
    );
    const submitted = editedValues;
    setIsSaving(true);
    setSaveError("");
    try {
      const canvas = canvasRef.current;
      if (!canvas) throw new Error("The form is still loading. Try again.");
      // The editor's own PDF is the save, unless an edit sits in a field the
      // PDF has no widget for: only an Extend run can write that one.
      let updated: CasePrepUscisFilledForm;
      if (
        Object.keys(changedValues).some((name) => !canvas.fillsWidget(name))
      ) {
        updated = await CasePrepUscisFormsAPI.generateFilledForm(
          individualId,
          filledFormId,
          { field_values: changedValues }
        );
      } else {
        const pdf = await canvas.getDocumentBuffer();
        updated = await CasePrepUscisFormsAPI.saveFilledForm(
          individualId,
          filledFormId,
          { field_values: changedValues, pdf }
        );
        savedStoragePathRef.current = storagePathOf(updated);
      }
      carryOverRef.current = changedOverlayValues(
        editedValuesRef.current,
        submitted
      );
      setJustSaved(true);
      onUpdated(updated);
    } catch (error) {
      setSaveError(
        error instanceof Error ? error.message : "Failed to save changes."
      );
    } finally {
      setIsSaving(false);
    }
  };

  // A form that never produced a PDF has no overlay schema to type into. This
  // asks for the blank form's schema and PDF, which needs no AI fill.
  const initializeBlankForm = async () => {
    if (isInitializingBlank || isSaving) return;
    setIsInitializingBlank(true);
    setSaveError("");
    try {
      const updated = await CasePrepUscisFormsAPI.generateFilledForm(
        individualId,
        filledFormId,
        { initialize_blank: true }
      );
      onUpdated(updated);
    } catch (error) {
      setSaveError(
        error instanceof Error
          ? error.message
          : "Failed to prepare a blank form."
      );
    } finally {
      setIsInitializingBlank(false);
    }
  };

  const downloadGeneratedPdf = async () => {
    if (isDownloadingPdf || isSaving || form.status === "generating") return;
    setIsDownloadingPdf(true);
    try {
      const blob = await CasePrepUscisFormsAPI.getFilledFormPdf(
        individualId,
        filledFormId
      );
      downloadPdfBlob(blob, `${form.form_number || "filled-form"}.pdf`);
    } catch (error) {
      toast.error("Unable to download PDF", {
        description:
          error instanceof Error
            ? error.message
            : "Please try again in a moment.",
        duration: 5000,
        closeButton: true
      });
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  // Unsaved typing lives only in ``editedValues``, and a run rewrites the form
  // from what the server already has, so it would silently throw that typing away.
  const isAutofillDisabled =
    hasUnsavedChanges ||
    isSaving ||
    isInitializingBlank ||
    isGeneratedPdfLoading ||
    (form.status === "generating" && !form.run_stalled);

  const selectField = (fieldName: string) => {
    setSelectedFieldName(fieldName);
    canvasRef.current?.scrollToField(fieldName);
  };

  const citation = useCitationOverlay();
  const resolveUser = useFieldSourceUsers(provenanceItems);
  const itemsByField = useMemo(
    () => new Map(provenanceItems.map((item) => [item.fieldName, item])),
    [provenanceItems]
  );
  const legendKeyByField = useMemo(() => {
    const byField = new Map<string, SourceLegendKey>();
    for (const item of provenanceItems) {
      const key = sourceLegendKeyOf(item.provenance);
      if (key) byField.set(item.fieldName, key);
    }
    return byField;
  }, [provenanceItems]);
  const legendCounts = useMemo(() => {
    const counts: Partial<Record<SourceLegendKey, number>> = {};
    for (const key of legendKeyByField.values()) {
      counts[key] = (counts[key] ?? 0) + 1;
    }
    return counts;
  }, [legendKeyByField]);
  const shownIsolatedKind =
    isolatedKind && legendCounts[isolatedKind] ? isolatedKind : null;

  const hasCard = React.useCallback(
    (fieldName: string) => Boolean(itemsByField.get(fieldName)?.provenance),
    [itemsByField]
  );
  const hoverCard = useFieldHoverCard({ enabled: true, hasCard });
  const hoverCardItem = hoverCard.shown
    ? itemsByField.get(hoverCard.shown) ?? null
    : null;

  /** A deep-linked field to scroll to once the PDF is on screen. */
  const pendingScrollRef = useRef<string | null>(null);
  const formComments = useFormCommentsBridge({
    individualId,
    filledFormId,
    paneShown: isCommentsOpen,
    show: (fieldName, { scroll }) => {
      hoverCard.close();
      setIsCommentsOpen(true);
      setSelectedFieldName(fieldName);
      if (scroll) pendingScrollRef.current = fieldName;
    }
  });
  const commentPins = formComments.pins;
  const openCommentThreadRef = useRef(formComments.openThread);
  openCommentThreadRef.current = formComments.openThread;

  const renderSourceStripe = React.useCallback(
    ({ field, rect }: FormCanvasFieldOverlayProps) => {
      const key = legendKeyByField.get(field.field_name);
      if (!key || (shownIsolatedKind && key !== shownIsolatedKind)) return null;
      // Just outside the box, so it never covers the value.
      return (
        <span
          aria-hidden="true"
          data-slot="form-source-stripe"
          data-source-kind={key}
          className={cn(
            "absolute rounded-xs",
            SOURCE_LEGEND_BY_KEY[key].stripe
          )}
          style={{
            left: rect.left - 4,
            top: rect.top,
            width: 3,
            height: rect.height
          }}
        />
      );
    },
    [legendKeyByField, shownIsolatedKind]
  );

  const renderFieldOverlay = React.useCallback(
    (props: FormCanvasFieldOverlayProps) => {
      const stripe = renderSourceStripe(props);
      const pin = renderCommentPin(
        props,
        commentPins[props.field.field_name],
        (threadId) => openCommentThreadRef.current(threadId)
      );
      return stripe || pin ? (
        <>
          {stripe}
          {pin}
        </>
      ) : null;
    },
    [renderSourceStripe, commentPins]
  );

  const handleFieldClick = (target: FormCanvasFieldTarget) => {
    const fieldName = target.field.field_name;
    hoverCard.togglePin(fieldName);
    // An empty field can still be commented on.
    if (isCommentsOpen) setSelectedFieldName(fieldName);
  };

  /** Filled fields in reading order, then empty ones, so any field can take a thread. */
  const commentFields = useMemo<FieldSourceItem[]>(
    () => [
      ...provenanceItems,
      ...overlayFields
        .filter((field) => !itemsByField.has(field.field_name))
        .map((field) => ({
          fieldName: field.field_name,
          question: field.question,
          pdfLabel: field.pdf_label,
          formText: field.form_text,
          value: null,
          pageNumber:
            typeof field.page_index === "number"
              ? field.page_index + 1
              : undefined
        }))
    ],
    [provenanceItems, overlayFields, itemsByField]
  );

  const handleFieldFocus = (
    target: FormCanvasFieldTarget,
    { keyboard }: { keyboard: boolean }
  ) => {
    const fieldName = target.field.field_name;
    if (editingFieldRef.current === fieldName) {
      editingFieldRef.current = null;
      return;
    }
    if (keyboard) hoverCard.focusField(fieldName);
  };

  const isCanvasTarget = React.useCallback(
    (target: EventTarget | null) =>
      target instanceof Node &&
      Boolean(canvasHostRef.current?.contains(target)),
    []
  );

  const canOpenSource = (item: FieldSourceItem) => {
    const target = fieldSourceTarget(item);
    if (target?.kind === "case_file") return Boolean(caseIde);
    if (target?.kind === "intake") return Boolean(form.foreign_national_id);
    return false;
  };

  const openSource = (item: FieldSourceItem) => {
    const target = fieldSourceTarget(item);
    if (target?.kind === "case_file" && caseIde) {
      caseIde.open({
        kind: "file",
        id: target.nodeId,
        title: target.title,
        page: target.page
      });
      return;
    }
    const clientId = form.foreign_national_id;
    if (target?.kind !== "intake" || !clientId) return;
    const href = target.submissionId
      ? submissionPath(clientId, target.intakeId, target.submissionId)
      : intakePath(clientId, target.intakeId);
    // Leaving the screen would drop unsaved typing.
    if (hasUnsavedChanges) window.open(href, "_blank", "noopener");
    else navigate(href);
  };

  /** Opens the field's open thread, or starts one on it. */
  const commentOnField = (fieldName: string) =>
    formComments.commentOn(fieldName);

  const editFieldValue = (fieldName: string) => {
    hoverCard.close();
    editingFieldRef.current = fieldName;
    canvasRef.current?.focusField(fieldName);
  };

  const needsInput = form.needs_input ?? [];
  const needsInputFieldNames = useMemo(
    () => new Set((form.needs_input ?? []).map((item) => item.field_name)),
    [form.needs_input]
  );

  const isViewerShown =
    form.status !== "generating" &&
    !isInitializingBlank &&
    !isGeneratedPdfLoading &&
    !generatedPdfGone &&
    !generatedPdfError &&
    Boolean(generatedPdf);

  useEffect(() => {
    const fieldName = pendingScrollRef.current;
    if (!isViewerShown || !fieldName) return;
    pendingScrollRef.current = null;
    // After the page has laid out the panel the link just opened.
    requestAnimationFrame(() => canvasRef.current?.scrollToField(fieldName));
  }, [isViewerShown, selectedFieldName]);

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-surface">
      <div className="@container flex h-9 w-full flex-none items-center justify-between gap-3 border-b border-line-1 px-2">
        <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
          <FormStatusBadge status={form.status} />
          {isViewerShown ? (
            <SourcesLegend
              counts={legendCounts}
              isolated={shownIsolatedKind}
              onIsolate={setIsolatedKind}
            />
          ) : null}
        </div>
        <div className="flex flex-none items-center gap-2">
          {saveError ? (
            <span className="hidden max-w-60 truncate whitespace-nowrap text-sm text-danger @3xl:inline">
              {saveError}
            </span>
          ) : isSaving ? (
            <span
              role="status"
              className="hidden items-center gap-1.5 whitespace-nowrap text-sm text-fg-3 @3xl:inline-flex"
            >
              <Spinner className="size-3" />
              Saving…
            </span>
          ) : hasUnsavedChanges ? (
            <span className="hidden whitespace-nowrap text-sm font-medium text-warn @3xl:inline">
              Unsaved Changes
            </span>
          ) : justSaved ? (
            <span
              role="status"
              className="hidden items-center gap-1.5 whitespace-nowrap text-sm text-fg-3 @3xl:inline-flex"
            >
              <Check className="size-3.5 text-success" />
              Saved
            </span>
          ) : (
            <span className="hidden whitespace-nowrap text-sm text-fg-3 @5xl:inline">
              Last saved {formatDateTimeWithTimezone(lastSavedAt)}
            </span>
          )}
          <Button
            type="button"
            variant="secondary"
            disabled={isAutofillDisabled}
            title={
              hasUnsavedChanges
                ? "Save your changes before autofilling, or they will be lost."
                : undefined
            }
            onClick={() => setIsAutofillOpen(true)}
          >
            <Sparkles />
            Autofill
          </Button>
          <Button
            type="button"
            variant="primary"
            disabled={!hasUnsavedChanges || isSaving || isGeneratedPdfLoading}
            onClick={saveChanges}
            className="transition-opacity duration-150"
          >
            {isSaving ? <Spinner className="size-3.5" /> : <Save />}
            Save Changes
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label={`More actions for ${
                  form.form_number || "USCIS form"
                }`}
              >
                <MoreVertical />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="z-10000 w-56">
              <DropdownMenuItem onSelect={() => setIsVersionHistoryOpen(true)}>
                <Clock />
                View Version History
              </DropdownMenuItem>
              {moreMenuItems}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="min-h-0 min-w-0 flex-1 overflow-hidden">
        {form.status === "generating" && form.run_stalled ? (
          <EmptyState
            icon={FileWarning}
            className="h-full"
            title="This Fill Stopped Responding"
            description={
              <span className="block max-w-105">
                {`The last update was a while ago, so the fill likely stopped. The form's answers are as they were; autofill ${form.form_number} again to restart it.`}
              </span>
            }
            action={
              <Button
                type="button"
                variant="secondary"
                disabled={hasUnsavedChanges || isSaving}
                onClick={() => setIsAutofillOpen(true)}
              >
                <Sparkles />
                Autofill Again
              </Button>
            }
          />
        ) : form.status === "generating" || isInitializingBlank ? (
          <LoadingState
            className="h-full w-full bg-surface"
            text={
              isInitializingBlank
                ? "Preparing blank form..."
                : `${fillingLabel(form)}…`
            }
          />
        ) : isGeneratedPdfLoading ? (
          <OpeningFilledFormPreview />
        ) : (!form.generated_pdf_url &&
            !form.generated_pdf_file_id &&
            !storedPdfPath) ||
          generatedPdfGone ? (
          form.status === "failed" ||
          form.status === "not_started" ||
          generatedPdfGone ? (
            <EmptyState
              icon={FileWarning}
              className="h-full"
              title={
                isNeverFilled(form) && !form.last_run_error
                  ? `Fill ${form.form_number} From This Case's Evidence`
                  : generatedPdfGone
                  ? "This Form's PDF Is No Longer Available"
                  : "This Form Has No Generated PDF"
              }
              description={
                <span className="block max-w-105">
                  {isNeverFilled(form) && !form.last_run_error
                    ? `Autofill reads the case's evidence and intakes, or start from a blank copy and type the answers in yourself.`
                    : generatedPdfGone
                    ? `The answers are saved. Fill ${form.form_number} again to regenerate the PDF, or start from a blank copy.`
                    : form.last_run_error
                    ? `The last autofill didn't fill it: ${form.last_run_error}`
                    : `Fill ${form.form_number} from this case's evidence and intakes, or start from a blank copy and type the answers in yourself.`}
                </span>
              }
              action={
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={isAutofillDisabled}
                    onClick={() => setIsAutofillOpen(true)}
                  >
                    <Sparkles />
                    Autofill
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={isInitializingBlank}
                    onClick={initializeBlankForm}
                  >
                    {isInitializingBlank ? (
                      <Spinner className="size-3.5" />
                    ) : (
                      <FilePlus />
                    )}
                    Start Blank Form
                  </Button>
                </div>
              }
            />
          ) : (
            // A never-started (bare-draft) form opens straight into the shared
            // fill wizard for a unified experience, rather than a static screen.
            <>
              <div className="h-full w-full bg-surface" />
              <FillFormWizard
                isOpen={true}
                onClose={onBack}
                individualId={individualId}
                caseId={form.case_id}
                existingForm={form}
                initialStep="information"
                onCreated={onUpdated}
              />
            </>
          )
        ) : generatedPdfError || !generatedPdf ? (
          <EmptyState
            icon={FileWarning}
            className="h-full"
            title="Preview Unavailable"
            description={generatedPdfError || "Unable to render this PDF."}
          />
        ) : (
          <FormCitationDocumentsProvider
            individualId={individualId}
            filledFormId={filledFormId}
          >
            <div className="flex h-full min-w-0">
              <div className="flex min-w-0 flex-1 flex-col">
                {form.last_run_error &&
                dismissedRunError !== form.last_run_error ? (
                  <div
                    role="status"
                    className="flex flex-none items-start gap-2 border-b border-line-1 bg-warn-soft px-3 py-2 text-sm text-warn"
                  >
                    <AlertTriangle
                      className="mt-0.5 size-3.5 shrink-0"
                      aria-hidden
                    />
                    <p className="min-w-0 flex-1">
                      The last autofill didn't change this form:{" "}
                      {form.last_run_error}
                    </p>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-2xs"
                      aria-label="Dismiss"
                      onClick={() =>
                        setDismissedRunError(form.last_run_error ?? null)
                      }
                    >
                      <X />
                    </Button>
                  </div>
                ) : null}
                <NeedsInputPanel
                  items={needsInput}
                  values={editedValues}
                  onChange={(fieldName, value) =>
                    setEditedValues((prev) => ({ ...prev, [fieldName]: value }))
                  }
                  onLocate={selectField}
                />
                <div
                  ref={canvasHostRef}
                  className="min-h-0 flex-1"
                  onBlur={(event) => {
                    const next = event.relatedTarget;
                    if (
                      next instanceof Element &&
                      next.closest("[data-slot=popover-content]")
                    )
                      return;
                    hoverCard.blurField();
                  }}
                >
                  <FormCanvas
                    ref={canvasRef}
                    file={generatedPdf}
                    fileName={`${form.form_number || "filled-form"}.pdf`}
                    fields={overlayFields as FormCanvasField[]}
                    pdfValues={savedOverlayValues}
                    values={editedValues}
                    onValuesChange={(changed) =>
                      setEditedValues((prev) => ({ ...prev, ...changed }))
                    }
                    selectedFieldName={selectedFieldName}
                    needsInputFieldNames={needsInputFieldNames}
                    renderFieldOverlay={renderFieldOverlay}
                    onFieldHover={(target) =>
                      hoverCard.hoverField(target?.field.field_name ?? null)
                    }
                    onFieldClick={handleFieldClick}
                    onFieldFocus={handleFieldFocus}
                    onLoadError={setGeneratedPdfError}
                    toolbarActions={
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-xs"
                        aria-label="Download PDF"
                        title="Download PDF"
                        disabled={isDownloadingPdf}
                        onClick={downloadGeneratedPdf}
                      >
                        {isDownloadingPdf ? (
                          <Spinner className="size-3.5" />
                        ) : (
                          <Download />
                        )}
                      </Button>
                    }
                  />
                </div>
              </div>
              {isCommentsOpen ? (
                <FormSidePanel
                  commentCount={formComments.counts.open}
                  onClose={() => setIsCommentsOpen(false)}
                >
                  <FormCommentsPanel
                    individualId={individualId}
                    filledFormId={filledFormId}
                    selectedFieldName={selectedFieldName}
                    onSelectField={selectField}
                    fields={commentFields}
                    draftFieldName={formComments.draftFieldName}
                    onDraftDone={formComments.clearDraft}
                    activeThreadId={formComments.activeThreadId}
                    onActiveThreadChange={formComments.setActiveThreadId}
                    filter={formComments.filter}
                    onFilterChange={formComments.setFilter}
                  />
                </FormSidePanel>
              ) : null}
            </div>
            <FieldHoverCardLayer
              item={hoverCardItem}
              pinned={hoverCard.pinned}
              getAnchorRect={() =>
                hoverCard.shown
                  ? canvasRef.current?.getFieldClientRect(hoverCard.shown) ??
                    null
                  : null
              }
              isOwnTarget={isCanvasTarget}
              resolveUser={resolveUser}
              openCitationFieldName={citation.openFieldName}
              onToggleCitation={(next) => {
                hoverCard.close();
                citation.toggle(next);
              }}
              onOpenSource={
                hoverCardItem && canOpenSource(hoverCardItem)
                  ? () => openSource(hoverCardItem)
                  : undefined
              }
              onComment={
                hoverCardItem
                  ? () => commentOnField(hoverCardItem.fieldName)
                  : undefined
              }
              onEditValue={
                hoverCardItem
                  ? () => editFieldValue(hoverCardItem.fieldName)
                  : undefined
              }
              onClose={hoverCard.close}
              onPointerEnter={hoverCard.keepOpen}
              onPointerLeave={() => {
                if (!hoverCard.pinned) hoverCard.scheduleClose();
              }}
            />
            {citation.openCitation ? (
              <CitationDocumentOverlay
                key={citation.openCitation.fieldName}
                file={citation.openCitation.file}
                geometry={citation.openCitation.geometry}
                page={citation.openCitation.page}
                filename={citation.openCitation.filename}
                isOpen={!citation.isClosing}
                onClose={citation.close}
              />
            ) : null}
          </FormCitationDocumentsProvider>
        )}
      </div>

      {isAutofillOpen ? (
        <AutofillFormDrawer
          isOpen={true}
          onClose={() => setIsAutofillOpen(false)}
          individualId={individualId}
          form={form}
          onStarted={onUpdated}
        />
      ) : null}

      <FormVersionHistory
        open={isVersionHistoryOpen}
        onOpenChange={setIsVersionHistoryOpen}
        individualId={individualId}
        filledFormId={filledFormId}
        formTitle={form.form_title || form.form_number}
        onRestored={onUpdated}
      />
    </div>
  );
}

function RunHistoryModal({
  individualId,
  form,
  onClose
}: {
  individualId: string;
  form: CasePrepUscisFilledForm | null;
  onClose: () => void;
}) {
  const [selectedStep, setSelectedStep] = useState<CasePrepUscisRunStep | null>(
    null
  );
  const [selectedStepPdf, setSelectedStepPdf] = useState<Blob | null>(null);
  const [isStepPdfLoading, setIsStepPdfLoading] = useState(false);
  const [stepPdfError, setStepPdfError] = useState("");
  const [isFilledValuesOpen, setIsFilledValuesOpen] = useState(false);
  const steps = form?.run_steps ?? [];
  const isOpen = Boolean(form);
  const completedAt =
    form?.generated_at ??
    steps.find((step) => step.completed_at)?.completed_at ??
    form?.updated_at;
  const selectedStepIndex = selectedStep
    ? steps.findIndex((step) => step.step_id === selectedStep.step_id)
    : -1;
  const selectedStepNewlyFilledEntries =
    selectedStepIndex >= 0
      ? newlyFilledValueEntriesForStep(steps, selectedStepIndex)
      : [];
  const selectedStepNewlyFilledJson = Object.fromEntries(
    selectedStepNewlyFilledEntries
  );

  useEffect(() => {
    setSelectedStep(null);
    setSelectedStepPdf(null);
    setIsStepPdfLoading(false);
    setStepPdfError("");
    setIsFilledValuesOpen(false);
  }, [form?.case_extend_uscis_filled_form_id, steps]);

  useEffect(() => {
    setIsFilledValuesOpen(false);
  }, [selectedStep?.step_id]);

  useEffect(() => {
    if (
      !form?.case_extend_uscis_filled_form_id ||
      !selectedStep?.step_id ||
      (!selectedStep.output_pdf_url && !selectedStep.output_file_id)
    ) {
      setSelectedStepPdf(null);
      setIsStepPdfLoading(false);
      setStepPdfError("");
      return undefined;
    }

    let isMounted = true;
    setSelectedStepPdf(null);
    setStepPdfError("");
    setIsStepPdfLoading(true);
    CasePrepUscisFormsAPI.getRunStepPdf(
      individualId,
      form.case_extend_uscis_filled_form_id,
      selectedStep.step_id
    )
      .then((blob) => {
        if (!isMounted) return;
        setSelectedStepPdf(blob);
      })
      .catch((error) => {
        if (!isMounted) return;
        setStepPdfError(
          error instanceof Error
            ? error.message
            : "Unable to load run step PDF."
        );
      })
      .finally(() => {
        if (!isMounted) return;
        setIsStepPdfLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [form?.case_extend_uscis_filled_form_id, individualId, selectedStep]);

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <SheetContent
        side="right"
        showCloseButton={false}
        className="h-screen max-h-screen min-w-0 items-stretch gap-0 border-l-0 bg-transparent p-0 shadow-none data-[side=right]:w-screen data-[side=right]:border-l-0 data-[side=right]:sm:max-w-none"
        onPointerDown={(event) => {
          event.stopPropagation();
          if (!selectedStep && event.target === event.currentTarget) onClose();
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <div
          className="flex h-full w-full min-w-0 items-stretch justify-end gap-1.5 p-2"
          onPointerDown={(event) => {
            if (!selectedStep && event.target === event.currentTarget)
              onClose();
          }}
        >
          {selectedStep ? (
            <div className="hidden h-full min-w-0 flex-none items-stretch md:flex md:w-[55vw] md:max-w-205">
              <div className="h-full w-full min-w-0 overflow-hidden rounded-xl bg-elevated shadow-dialog ring-1 ring-line-3">
                <div className="flex h-full w-full min-h-0 min-w-0 flex-col">
                  <div className="flex flex-none items-start justify-between gap-3 border-b border-line-2 px-4 py-3">
                    <div className="min-w-0 flex-1">
                      <span className="block whitespace-pre-wrap wrap-break-word text-base font-medium text-fg">
                        Step {selectedStep.index}:{" "}
                        {sourceDetailLabel(selectedStep.source)}
                      </span>
                      <span className="block text-sm text-fg-3">
                        Completed {formatDate(selectedStep.completed_at)}
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Close preview"
                      onClick={() => setSelectedStep(null)}
                    >
                      <X />
                    </Button>
                  </div>
                  <div className="flex flex-none flex-col border-b border-line-2 px-4 py-2.5">
                    <button
                      type="button"
                      className="flex w-full items-center justify-between gap-3 rounded-xs text-left outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                      onClick={() =>
                        setIsFilledValuesOpen((currentValue) => !currentValue)
                      }
                    >
                      <span className="text-sm font-medium text-fg">
                        Filled JSON
                      </span>
                      <span className="flex items-center gap-2 text-sm text-fg-3">
                        {selectedStepNewlyFilledEntries.length} fields
                        {isFilledValuesOpen ? (
                          <ChevronUp className="size-3.5" />
                        ) : (
                          <ChevronDown className="size-3.5" />
                        )}
                      </span>
                    </button>
                    {isFilledValuesOpen ? (
                      selectedStepNewlyFilledEntries.length === 0 ? (
                        <span className="pt-2 text-sm text-fg-3">
                          No fields filled by this step.
                        </span>
                      ) : (
                        <pre className="mt-2 max-h-45 overflow-auto rounded-md bg-fill-1 p-3 font-mono text-sm text-fg">
                          {JSON.stringify(selectedStepNewlyFilledJson, null, 2)}
                        </pre>
                      )
                    ) : null}
                  </div>
                  <div className="min-h-0 flex-1 bg-fill-1">
                    {isStepPdfLoading ? (
                      <LoadingState
                        className="h-full w-full"
                        text="Loading preview..."
                      />
                    ) : stepPdfError ? (
                      <EmptyState
                        icon={FileWarning}
                        className="h-full"
                        title="Preview Unavailable"
                        description={stepPdfError}
                      />
                    ) : (
                      <PdfPreview
                        file={selectedStepPdf}
                        emptyText="No PDF artifact is available for this step."
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          <div className="relative h-full w-110 max-w-[92vw] overflow-hidden rounded-xl bg-elevated shadow-dialog ring-1 ring-line-3">
            <div className="flex h-full w-full flex-col items-start">
              <div className="flex w-full items-center justify-start gap-3 border-b border-line-2 px-4 py-3">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Close run history"
                  onClick={onClose}
                >
                  <PanelRightClose />
                </Button>
                <div className="flex min-w-0 flex-col items-start">
                  <SheetTitle className="truncate text-base font-medium text-fg">
                    Run History
                  </SheetTitle>
                  <SheetDescription className="truncate text-sm text-fg-3">
                    {form?.form_number} completed on {formatDate(completedAt)}
                  </SheetDescription>
                </div>
              </div>

              <div className="flex min-h-0 w-full flex-1 flex-col items-start overflow-y-auto p-4">
                {steps.length === 0 ? (
                  <EmptyState
                    icon={History}
                    className="w-full"
                    title="No Run History Yet"
                  />
                ) : (
                  <div className="flex w-full flex-col gap-6">
                    {steps.map((step, index) => {
                      const newlyFilledEntries = newlyFilledValueEntriesForStep(
                        steps,
                        index
                      );
                      const isSelected = selectedStep?.step_id === step.step_id;
                      const isLast = index === steps.length - 1;
                      const StepStatusIcon = runStepStatusIcon(step.status);
                      return (
                        <div
                          key={step.step_id}
                          className="relative flex w-full min-w-0 items-stretch"
                        >
                          {!isLast ? (
                            <div className="absolute -bottom-6.5 left-5.75 top-9 w-px bg-line-2" />
                          ) : null}
                          <button
                            type="button"
                            className={cn(
                              "relative z-1 flex w-full min-w-0 items-start gap-2.5 rounded-lg bg-elevated px-3 py-2 text-left ring-1 transition-colors duration-100 outline-none hover:bg-fill-1 focus-visible:ring-2 focus-visible:ring-primary/40",
                              isSelected
                                ? "bg-accent-soft ring-accent-line hover:bg-accent-soft"
                                : "ring-line-2"
                            )}
                            onClick={() => setSelectedStep(step)}
                          >
                            <div
                              className={cn(
                                "relative z-2 mt-0.5 flex size-6 flex-none items-center justify-center rounded-full",
                                runStepStatusIconClass(step.status)
                              )}
                            >
                              <StepStatusIcon className="size-3.5" />
                            </div>
                            <div className="flex min-w-0 flex-1 flex-col items-start gap-1 pt-0.5">
                              <div className="flex w-full min-w-0 items-center gap-2">
                                <span
                                  className={cn(
                                    "min-w-0 text-base text-fg",
                                    isSelected
                                      ? "whitespace-pre-wrap wrap-break-word"
                                      : "truncate"
                                  )}
                                >
                                  <span className="font-medium">
                                    {sourceStepLeadIn(index, steps.length)}
                                  </span>{" "}
                                  {isSelected
                                    ? sourceDetailLabel(step.source)
                                    : sourceCardLabel(step.source)}
                                </span>
                                <SourceTypeTag type={step.source.source_type} />
                              </div>
                              <span className="text-sm text-fg-3">
                                {step.failure_message
                                  ? step.failure_message
                                  : `${newlyFilledEntries.length} fields filled`}
                              </span>
                            </div>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
